import { Text } from '@cinch-labs/design-system'
import { interpolateI18n } from '@cinch-labs/shared-util'

const interpolateText = (template: string, displayTable: boolean) =>
  interpolateI18n(
    template,
    {},
    {
      b: (text: string) =>
        displayTable ? (
          <Text element="span" fontWeight="bold" fontSizeFixed="xs" key={text}>
            {text}
          </Text>
        ) : (
          text
        ),
    },
  )

interface GenerateFinanceRepresentativeExampleContentProps {
  displayTable: boolean
  annualMileageLimit: string
  creditAmount: string
  customerDeposit?: string
  excessMileageChargeInPence: number
  financeDepositContribution?: string
  fixedAnnualInterestRate: string
  monthlyPaymentsAmount: string
  numberOfMonthlyPayments: number | string
  onTheRoadPrice: string
  optionalFinalPayment: string
  optionToPurchaseFee?: string
  representativeApr: string
  retailerDiscount?: string
  testDriveVoucher?: string
  totalAmountPayable: string
  totalInterestAmount: string
}

export const generateFinanceRepresentativeExampleContent = ({
  displayTable,
  annualMileageLimit,
  creditAmount,
  customerDeposit,
  excessMileageChargeInPence,
  financeDepositContribution,
  fixedAnnualInterestRate,
  monthlyPaymentsAmount,
  numberOfMonthlyPayments,
  onTheRoadPrice,
  optionalFinalPayment,
  optionToPurchaseFee,
  representativeApr,
  retailerDiscount,
  testDriveVoucher,
  totalAmountPayable,
  totalInterestAmount,
}: GenerateFinanceRepresentativeExampleContentProps) => {
  const baseTextSnippets = [
    `Based on an on the road price of {b}${onTheRoadPrice}{/b}`,
    ...(customerDeposit
      ? [`with a {b}${customerDeposit}{/b} customer deposit`]
      : []),
    ...(financeDepositContribution
      ? [
          `plus a {b}${financeDepositContribution}{/b} finance deposit contribution`,
        ]
      : []),
    ...(testDriveVoucher
      ? [
          `plus a {b}${testDriveVoucher}{/b} Test Drive Voucher (automatically applied to all purchases)`,
        ]
      : []),
    `and borrowing {b}${creditAmount}{/b} at a representative APR of {b}${representativeApr}{/b}`,
  ]

  const extendedTextSnippets = [
    `${numberOfMonthlyPayments} monthly payments of ${monthlyPaymentsAmount}. Fixed rate of interest per annum ${fixedAnnualInterestRate}. Optional final payment ${optionalFinalPayment}. Amount of interest ${totalInterestAmount}. Total amount payable ${totalAmountPayable}. Annual mileage limit ${annualMileageLimit} miles. Excess mileage charge ${excessMileageChargeInPence}p per mile.`,
    ...(optionToPurchaseFee
      ? [`Option-to-purchase fee ${optionToPurchaseFee}.`]
      : []),
    ...(retailerDiscount
      ? [
          `On the road price includes a retailer discount of ${retailerDiscount}.`,
        ]
      : []),
  ]

  const baseText = baseTextSnippets.join(', ').replace(', and', ' and')
  const extendedText = extendedTextSnippets.join(' ')

  const text = interpolateText(
    displayTable
      ? `${baseText} and the following:`
      : `${baseText}. ${extendedText}`,
    displayTable,
  )

  const tableData = [
    {
      term: `${numberOfMonthlyPayments} monthly payments of`,
      details: `${monthlyPaymentsAmount}`,
    },
    {
      term: 'Fixed rate of interest per annum',
      details: `${fixedAnnualInterestRate}`,
    },
    {
      term: 'Optional final payment',
      details: `${optionalFinalPayment}`,
    },
    {
      term: 'Amount of interest',
      details: `${totalInterestAmount}`,
    },
    {
      term: 'Total amount payable',
      details: `${totalAmountPayable}`,
    },
    {
      term: 'Annual mileage limit',
      details: `${annualMileageLimit} miles`,
    },
    {
      term: 'Excess mileage charge',
      details: `${excessMileageChargeInPence}p per mile`,
    },
    ...(optionToPurchaseFee
      ? [
          {
            term: 'Option-to-purchase fee',
            details: `${optionToPurchaseFee}`,
          },
        ]
      : []),
    ...(retailerDiscount
      ? [
          {
            term: 'Retailer discount',
            details: `${retailerDiscount}`,
          },
        ]
      : []),
  ]

  return {
    text,
    tableData,
  }
}
